import React from 'react';
import { SPM_FIGURE_METADATA } from '../data/ipccData';
import { BarChart3, Globe, Layers, TrendingUp, Compass, Sliders, Zap, Shield, Grid, Clock, Flame, Sparkles, Music } from 'lucide-react';

interface NavigationProps {
  activeReport: 'ar6' | 'sr15';
  activeFigureId: string;
  onSelectFigure: (id: string) => void;
}

const FIGURE_ICONS: Record<string, React.ReactNode> = {
  'spm1': <TrendingUp className="w-4 h-4" />,
  'spm2': <BarChart3 className="w-4 h-4" />,
  'spm3': <Globe className="w-4 h-4" />,
  'spm4': <Layers className="w-4 h-4" />,
  'spm5': <Compass className="w-4 h-4" />,
  'spm6': <Sliders className="w-4 h-4" />,
  'spm7': <Zap className="w-4 h-4" />,
  'spm8': <Shield className="w-4 h-4" />,
  'spm9': <Grid className="w-4 h-4" />,
  'spm10': <Clock className="w-4 h-4" />,
  // SR15 Figures
  'sr15_spm1': <TrendingUp className="w-4 h-4 text-amber-400" />,
  'sr15_spm2': <Layers className="w-4 h-4 text-emerald-400" />,
  'sr15_spm3': <Flame className="w-4 h-4 text-red-400" />,
  'sr15_spm4': <Grid className="w-4 h-4 text-cyan-400" />
};

export const Navigation: React.FC<NavigationProps> = ({
  activeReport,
  activeFigureId,
  onSelectFigure
}) => {
  const allFigures = Object.values(SPM_FIGURE_METADATA);
  const isIndividualFigureActive = activeFigureId !== 'symphony' && !activeFigureId.startsWith('sr15_');

  return (
    <nav className="w-full bg-slate-950/90 border-b border-slate-800/80 px-4 py-2.5">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        {activeReport === 'ar6' ? (
          <div className="flex items-center justify-between w-full flex-wrap gap-2">
            <button
              onClick={() => onSelectFigure('symphony')}
              className={`flex items-center gap-2.5 px-4 py-2 rounded-2xl text-xs font-bold transition-all border ${
                activeFigureId === 'symphony'
                  ? 'bg-cyan-500 text-slate-950 border-cyan-400 glow-cyan shadow-lg'
                  : 'bg-slate-900 text-slate-300 border-slate-800 hover:text-white'
              }`}
            >
              <Music className="w-4 h-4 text-slate-950 fill-current" />
              <span>🎵 Grand Climate Symphony (AR6 WGI Master Suite)</span>
            </button>

            {isIndividualFigureActive && (
              <button
                onClick={() => onSelectFigure('symphony')}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 transition-colors shadow-md"
              >
                <span>← Return to Grand Symphony</span>
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none min-w-max">
            {allFigures
              .filter((fig) => fig.id.startsWith('sr15_'))
              .map((fig) => {
                const isActive = fig.id === activeFigureId;
                return (
                  <button
                    key={fig.id}
                    onClick={() => onSelectFigure(fig.id)}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all border ${
                      isActive
                        ? 'bg-cyan-500 text-slate-950 border-cyan-400 glow-cyan shadow-md'
                        : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800 hover:text-slate-200'
                    }`}
                  >
                    <span>{FIGURE_ICONS[fig.id]}</span>
                    <span>{fig.number} &bull; {fig.title.split(' ')[0]}</span>
                  </button>
                );
              })}
          </div>
        )}
      </div>
    </nav>
  );
};
